import { useAllPiiBackfillStates } from "@/api/pii-vault/use-pii-backfill";
import { useMemo, useState } from "react";
import { PiiBackfillFilters } from "./pii-backfill-filters";
import { PiiBackfillLaunchBar } from "./pii-backfill-launch-bar";
import {
  type BackfillFilters,
  DEFAULT_FILTERS,
  matchesFilters,
} from "./pii-backfill-row-filter";
import { type BackfillShop, PiiBackfillShopRow } from "./pii-backfill-shop-row";
import { summarize } from "./pii-backfill-summary";

/** The API bounds one start request: each shop counts its documents first. */
const MAX_SHOPS_PER_START = 100;

/**
 * Super-admin launcher of the vault backfill across shops
 * (RGPD-PII-VAULT-PLAN.md, step 2). Smallest shops come first: they finish
 * fast, and every shop running at once shares the same queue.
 */
export function PiiBackfillTab({ shops }: { shops: BackfillShop[] }) {
  const overview = useAllPiiBackfillStates();
  const [filters, setFilters] = useState<BackfillFilters>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    const states = overview.data?.states ?? [];
    const now = new Date();
    return [...shops]
      .sort((a, b) => a.totalOrders - b.totalOrders)
      .map((shop) => ({
        shop,
        summary: summarize(states.filter((s) => s.shopId === shop.id)),
      }))
      .filter((row) => matchesFilters(row, filters, now));
  }, [shops, overview.data, filters]);

  const visible = rows.slice(0, MAX_SHOPS_PER_START);
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      <PiiBackfillFilters
        filters={filters}
        onChange={setFilters}
        visibleCount={visible.length}
        selectedCount={selected.size}
        onSelectVisible={() =>
          setSelected(new Set(visible.map(({ shop }) => shop.id)))
        }
        onClearSelection={() => setSelected(new Set())}
      />
      <PiiBackfillLaunchBar shopIds={[...selected]} />
      <div className="overflow-x-auto rounded-lg border bg-background">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2" />
              <th className="px-3 py-2">Shop</th>
              <th className="px-3 py-2 text-right">Commandes</th>
              <th className="px-3 py-2">Rattrapage</th>
              <th className="px-3 py-2">Progression</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ shop, summary }) => (
              <PiiBackfillShopRow
                key={shop.id}
                shop={shop}
                summary={summary}
                isSelected={selected.has(shop.id)}
                onToggle={() => toggle(shop.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
